// src/utils/dentallySite.js
//
// One CRM practice ↔ one Dentally site. The PracticeDentally config carries the
// configured site as `site: { id, name } | null` (status/capabilities payloads)
// or as flat `site_id` / `site_name` (integration serializer). These helpers
// normalise both shapes and stay tolerant of the fields being absent while the
// backend rolls out (docs/superpowers/plans/2026-09-18-dentally-site-scope.md).

export const ALL_SITES_OPTION = Object.freeze({ id: '', name: 'All sites (single-site account)' });

export const SITE_OUT_OF_SCOPE_CODE = 'site_out_of_scope';
export const SITE_OUT_OF_SCOPE_FALLBACK = "That site is outside this practice's Dentally site.";
export const SITE_HELPER_TEXT = "Only this site's patients, appointments and practitioners are shown, and bookings are created here.";
export const SITE_LOCKED_TOOLTIP = 'Set in Integration settings';
export const SITE_ACCOUNT_LEVEL_HELPER = 'Patients are account-level';

/**
 * Which Dentally resources the configured site actually scopes.
 *
 * Appointments, practitioners, diaries and rooms are site-level: the backend
 * resolves the configured site for them (`_resolve_site_id`), so the UI
 * preselects it and locks the field. PATIENTS are account-level — a patient's
 * home site is not their visit site — and the backend deliberately does NOT
 * default the configured site for `search_patients` (`_explicit_site_id`,
 * apps/dentally/client.py). Pre-filling and locking a site on the patient
 * search hid patients registered at sister sites and invited duplicates.
 */
export const DENTALLY_SITE_SCOPED_RESOURCES = Object.freeze([
  'appointments',
  'practitioners',
  'diaries',
  'rooms',
]);

/** True when the configured site scopes this resource server-side. */
export function isSiteScopedResource(resource) {
  return DENTALLY_SITE_SCOPED_RESOURCES.includes(resource);
}

/** Initial value for a resource's site filter (blank for account-level ones). */
export function defaultSiteFilter(resource, configuredSiteId) {
  if (!isSiteScopedResource(resource)) return '';
  return asId(configuredSiteId);
}

/** True when the resource's site filter must be preselected and read-only. */
export function siteFilterLocked(resource, configuredSiteId) {
  return isSiteScopedResource(resource) && Boolean(asId(configuredSiteId));
}

const asId = (value) => (value == null || value === '' ? '' : String(value));

/** Configured site for a Dentally config / status payload, or null when blank. */
export function siteFromConfig(config) {
  if (!config || typeof config !== 'object') return null;
  const nested = config.site && typeof config.site === 'object' ? config.site : null;
  const id = asId(nested?.id ?? config.site_id);
  if (!id) return null;
  const name = nested?.name ?? config.site_name;
  return { id, name: typeof name === 'string' && name.trim() ? name : id };
}

/** Normalise the `sites/` endpoint payload (`{ sites: [...] }` or a bare array). */
export function normaliseSites(data) {
  let list = [];
  if (Array.isArray(data)) list = data;
  else if (Array.isArray(data?.sites)) list = data.sites;
  return list
    .filter((site) => site && typeof site === 'object' && asId(site.id))
    .map((site) => ({ ...site, id: asId(site.id), name: typeof site.name === 'string' && site.name.trim() ? site.name : asId(site.id) }));
}

/** Label for a site id given the loaded sites (falls back to the id or a configured name). */
export function siteLabel(siteId, sites = [], fallbackName = null) {
  const id = asId(siteId);
  if (!id) return ALL_SITES_OPTION.name;
  const match = sites.find((site) => asId(site.id) === id);
  return match?.name || fallbackName || id;
}

/** True when a callApi response (or a Dentally read result) is a site_out_of_scope rejection. */
export function isSiteOutOfScope(responseOrResult) {
  if (!responseOrResult || typeof responseOrResult !== 'object') return false;
  if (responseOrResult.code === SITE_OUT_OF_SCOPE_CODE) return true;
  const { data } = responseOrResult;
  return Boolean(data && typeof data === 'object' && (data.code === SITE_OUT_OF_SCOPE_CODE || data.error_code === SITE_OUT_OF_SCOPE_CODE));
}

/** Server message for a site_out_of_scope rejection, with a plain fallback. */
export function siteOutOfScopeMessage(responseOrResult) {
  const data = responseOrResult?.data && typeof responseOrResult.data === 'object' ? responseOrResult.data : responseOrResult;
  const message = data?.message ?? data?.detail;
  return typeof message === 'string' && message.trim() ? message : SITE_OUT_OF_SCOPE_FALLBACK;
}
